const crypto = require('crypto');

// Configurações de criptografia
const ENCRYPTION_ALGORITHM = 'aes-256-cbc';
const HASH_ALGORITHM = 'sha256';
const IV_LENGTH = 16; // Para AES, o IV é sempre de 16 bytes

/**
 * Processa a chave de criptografia para garantir que tenha exatamente 32 bytes para AES-256
 * @param {string|Buffer} key - Chave de criptografia
 * @returns {Buffer} Chave processada com 32 bytes
 */
const processEncryptionKey = (key) => {
    if (Buffer.isBuffer(key)) {
        return key.length === 32 ? key : crypto.createHash('sha256').update(key).digest();
    }
    // Se for string, cria um hash SHA-256 que sempre resulta em 32 bytes
    return crypto.createHash('sha256').update(key).digest();
};

/**
 * Gera um hash SHA-256 dos dados
 * @param {Object|string} data - Dados para gerar o hash
 * @returns {string} Hash em formato hexadecimal
 */
const generateHash = (data) => {
    const dataString = typeof data === 'object' ? JSON.stringify(data) : data;
    return crypto.createHash(HASH_ALGORITHM).update(dataString).digest('hex');
};

/**
 * Criptografa os dados usando AES-256-CBC
 * @param {Object|string} data - Dados para criptografar
 * @param {string|Buffer} encryptionKey - Chave de criptografia
 * @returns {string} Dados criptografados em formato base64 com IV
 */
const encryptData = (data, encryptionKey) => {
    const processedKey = processEncryptionKey(encryptionKey);
    const dataString = typeof data === 'object' ? JSON.stringify(data) : data;
    const iv = crypto.randomBytes(IV_LENGTH);
    const cipher = crypto.createCipheriv(ENCRYPTION_ALGORITHM, processedKey, iv);
    
    let encrypted = cipher.update(dataString, 'utf8', 'base64');
    encrypted += cipher.final('base64');
    
    // Concatena o IV com os dados criptografados
    return iv.toString('base64') + ':' + encrypted;
};

/**
 * Descriptografa os dados usando AES-256-CBC
 * @param {string} encryptedData - Dados criptografados em formato base64 com IV
 * @param {string|Buffer} encryptionKey - Chave de criptografia
 * @returns {Object} Dados descriptografados
 */
const decryptData = (encryptedData, encryptionKey) => {
    const processedKey = processEncryptionKey(encryptionKey);
    const parts = encryptedData.split(':');
    if (parts.length !== 2) {
        throw new Error('Formato de dados criptografados inválido');
    }
    
    const iv = Buffer.from(parts[0], 'base64');
    const encrypted = parts[1];
    
    const decipher = crypto.createDecipheriv(ENCRYPTION_ALGORITHM, processedKey, iv);
    
    let decrypted = decipher.update(encrypted, 'base64', 'utf8');
    decrypted += decipher.final('utf8');
    
    try {
        return JSON.parse(decrypted);
    } catch (error) {
        // Se não for JSON válido, retorna como string
        return decrypted;
    }
};

/**
 * Verifica se o hash dos dados corresponde ao hash esperado
 * @param {Object|string} data - Dados originais
 * @param {string} expectedHash - Hash esperado
 * @returns {boolean} True se os hashes coincidirem
 */
const verifyHash = (data, expectedHash) => {
    const currentHash = generateHash(data);
    return currentHash === expectedHash;
};

/**
 * Cria um token seguro com dados criptografados e hash
 * @param {Object} tokenData - Dados do token
 * @param {string|Buffer} encryptionKey - Chave de criptografia
 * @returns {Object} Objeto com hash e dados criptografados
 */
const createSecureToken = (tokenData, encryptionKey) => {
    const hashData = generateHash(tokenData);
    const cipheredData = encryptData(tokenData, encryptionKey);
    
    return {
        hashData,
        cipheredData
    };
};

/**
 * Valida e decodifica um token seguro
 * @param {Object} secureToken - Token com hash e dados criptografados
 * @param {string|Buffer} encryptionKey - Chave de criptografia
 * @returns {Object} Dados originais do token se válidos
 * @throws {Error} Se o hash não for válido ou ocorrer erro na descriptografia
 */
const validateSecureToken = (secureToken, encryptionKey) => {
    const { hashData, cipheredData } = secureToken;
    
    if (!hashData || !cipheredData) {
        throw new Error('Token seguro inválido: campos obrigatórios ausentes');
    }
    
    // Descriptografa os dados
    const decryptedData = decryptData(cipheredData, encryptionKey);
    
    // Verifica se o hash é válido
    if (!verifyHash(decryptedData, hashData)) {
        throw new Error('Token seguro inválido: hash não confere');
    }
    
    return decryptedData;
};

module.exports = {
    generateHash,
    encryptData,
    decryptData,
    verifyHash,
    createSecureToken,
    validateSecureToken,
    processEncryptionKey
};